import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Grid,
  Paper,
  Fade,
  Tooltip as MuiTooltip,
} from "@mui/material";
import WayfinderLayout from "./WayfinderLayout";
import WayfinderCard from "./WayfinderCard";
import SubCategoryView from "./SubCategoryView";
import UseToast from "../UseToast";
import { getLanguageText } from "../../utils/multilanguageUtils";

const CategoryManagerPage = ({ buttonColor, currentLanguage }) => {
  const { userId } = useParams();
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [frontendCategories, setFrontendCategories] = useState([]);
  const [frontCategoryName, setFrontCategoryName] = useState("");
  const [frontendSubCategories, setFrontendSubCategories] = useState([]);
  const [showSubCategoryView, setShowSubCategoryView] = useState(false);
  const [selectedCategoryForSubView, setSelectedCategoryForSubView] = useState(null);
  const [selectedParentCategories, setSelectedParentCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [allSubCategories, setAllCategories] = useState([]);

  const fetchedData = useRef({ wayfinder: false });

  const fetchWayfinderData = async () => {
    if (fetchedData.current.wayfinder) return;

    setIsLoading(true);
    try {
      const categoriesResponse = await axios.get(
        `/api/wayfinder/get-categories/${userId}`,
      );
      if (!categoriesResponse.data.error) {
        const frontendCatNames = (categoriesResponse.data?.data || []).map(
          (fc) => ({
            ...fc,
            matchedCategoryName: fc?.linkedLocation
              ? `(${fc?.linkedLocation})`
              : "",
          }),
        );
        setFrontendCategories(frontendCatNames);
        setFrontendSubCategories(categoriesResponse.data?.data || []);
        setCategories(categoriesResponse.data?.data.categories || []);
      }

      const subcategoriesResponse = await axios.get(
        `/api/wayfinder/subcategory/${userId}`,
      );
      if (!subcategoriesResponse.data.error) {
        setAllCategories(subcategoriesResponse.data?.data || []);
      }

      fetchedData.current.wayfinder = true;
    } catch (error) {
      console.error("Error fetching wayfinder data:", error);
      showErrorToast("Failed to load wayfinder data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSubCategories = async (category) => {
    try {
      setSelectedCategoryForSubView(category);
      setShowSubCategoryView(true);
      setSearchTerm("");
      await handleFetchLinkingSubCategory(category._id);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFetchLinkingSubCategory = async (categoryId) => {
    try {
      if (categoryId) {
        const subcategoriesResponse = await axios.get(
          `/api/wayfinder/subcategory/category/${categoryId}`,
        );

        if (subcategoriesResponse.data.success) {
          const subCategoryData = subcategoriesResponse.data.dataSub || [];
          setFilteredSubCategories(subCategoryData);
        } else {
          setFilteredSubCategories([]);
        }
      }
    } catch (error) {
      console.error("Error fetching subcategories for category:", error);
      setFilteredSubCategories([]);
      showErrorToast("Failed to fetch subcategories");
    }
  };

  const handleCloseSubCategoryView = () => {
    setShowSubCategoryView(false);
    setSearchTerm("");
  };

  const toggleSubCategoryVisibility = (subcategoryId, isVisible) => {
    axios
      .patch(`/api/wayfinder/subcategory/${subcategoryId}/status`, {
        userId: userId,
        status: !isVisible,
      })
      .then((response) => {
        if (!response.data.error) {
          setFilteredSubCategories((prev) =>
            prev.map((sub) =>
              sub._id === subcategoryId ? { ...sub, status: !isVisible } : sub,
            ),
          );
          showMsgSuccessToast(
            `Subcategory ${!isVisible ? "activated" : "deactivated"} successfully!`,
          );
        } else {
          showErrorToast(response.data?.msg);
        }
      });
  };

  const handleUnlinkSubCategory = async (subcategoryId, categoryId) => {
    try {
      const response = await axios.post(`/api/wayfinder/subcategory/unlink`, {
        subcategoryId: subcategoryId,
        categoryId: categoryId,
        userId: userId,
      });

      if (response.data.success) {
        await handleFetchLinkingSubCategory(categoryId);
        showMsgSuccessToast("Subcategory unlinked successfully!");
      } else {
        showErrorToast(
          response.data?.message || "Failed to unlink subcategory",
        );
      }
    } catch (error) {
      console.error("Error unlinking subcategory:", error);
      showErrorToast("Failed to unlink subcategory");
    }
  };

  const deleteSubCategory = async (subcategoryId, categoryId) => {
    try {
      const response = await axios.delete(
        `/api/wayfinder/delete-wayfinder-subCategory/${subcategoryId}`,
        {
          data: { userId },
        },
      );

      if (!response.data.error) {
        await handleFetchLinkingSubCategory(categoryId);
        showMsgSuccessToast("Subcategory deleted successfully!");
      } else {
        showErrorToast(response.data?.msg);
      }
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      showErrorToast("Failed to delete subcategory");
    }
  };

  const toggleCategoryVisibility = (categoryId, isVisible, frontend = false) => {
    axios
      .patch(
        `/api/wayfinder/wayfinder-category-visibility/${userId}/${categoryId}/status`,
        {
          userId,
          categoryId,
          status: !isVisible,
          frontend,
        },
      )
      .then((response) => {
        if (response.data.success) {
          setFrontendCategories((prev) =>
            prev.map((cat) =>
              cat._id === categoryId ? { ...cat, status: !isVisible } : cat,
            ),
          );
          showMsgSuccessToast(
            `Category ${!isVisible ? "activated" : "deactivated"} successfully!`,
          );
        }
      })
      .catch(() => {
        showErrorToast("Failed to update category visibility");
      });
  };

  const deleteCategory = (categoryId, frontend = false) => {
    axios
      .delete(`/api/wayfinder/delete-wayfinder-category/${categoryId}`, {
        categoryId,
        userId: userId,
        frontend,
      })
      .then(() => {
        setFrontendCategories((prev) =>
          prev.filter((cat) => cat._id !== categoryId),
        );
        setCategories((prev) => prev.filter((cat) => cat._id !== categoryId));
        showMsgSuccessToast("Category deleted successfully!");
      })
      .catch((error) => {
        console.error("Error deleting category:", error);
        showErrorToast("Failed to delete category");
      });
  };

  const handleCreateSubCategoryFromView = async (subcategoryData, categoryId) => {
    try {
      const response = await axios.post("/api/wayfinder/subcategory", {
        userId: userId,
        ...subcategoryData,
        status: true,
        categoryId,
        currentLanguage: currentLanguage,
      });

      if (!response.data.error) {
        await handleFetchLinkingSubCategory(categoryId);

        const subcategoriesResponse = await axios.get(
          `/api/wayfinder/subcategory/${userId}`,
        );
        if (!subcategoriesResponse.data.error) {
          setAllCategories(subcategoriesResponse.data?.data || []);
        }

        showMsgSuccessToast("Subcategory created successfully!");
      } else {
        showErrorToast(response.data?.msg);
      }
    } catch (error) {
      console.error("Error creating subcategory:", error);
      showErrorToast("Failed to create subcategory");
    }
  };

  const handleLinkExistingSubCategory = async (subcategoryIds, categoryId) => {
    try {
      const response = await axios.post(
        `/api/wayfinder/subcategory/bulk-link`,
        {
          subcategoryIds: subcategoryIds,
          categoryId: categoryId,
          userId: userId,
        },
      );

      if (response.data.success) {
        const { linked, alreadyLinked } = response.data.data;

        await handleFetchLinkingSubCategory(categoryId);
        fetchWayfinderData();

        if (linked && linked.length > 0) {
          showMsgSuccessToast(
            `Successfully linked ${linked.length} subcategory(ies)`,
          );
        }
        if (alreadyLinked && alreadyLinked.length > 0) {
          showMsgSuccessToast(
            `${alreadyLinked.length} subcategory(ies) were already linked`,
          );
        }
      } else {
        showErrorToast(
          response.data?.message || "Failed to link subcategories",
        );
      }
    } catch (error) {
      console.error("Error linking subcategories:", error);
      showErrorToast("Failed to link subcategories");
    }
  };

  const createCategory = async (categoryName) => {
    if (!categoryName.trim()) return;

    try {
      const response = await axios.post("/api/wayfinder/categories", {
        name: categoryName,
        userId: userId,
        currentLanguage: currentLanguage,
        status: true,
      });

      if (response.data.success) {
        await fetchWayfinderData();
        showMsgSuccessToast("Category created successfully!");
        return response.data.data;
      } else {
        showErrorToast(response.data?.message || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      showErrorToast("Failed to create category");
    }
  };

  const filteredCategories = showSubCategoryView
    ? frontendCategories
    : frontendCategories.filter((cat) =>
        getLanguageText(cat.name, currentLanguage).toLowerCase().includes(searchTerm.toLowerCase()),
      );

  useEffect(() => {
    if (userId) {
      fetchWayfinderData();
    }
  }, [userId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Grid container spacing={3} justifyContent="center">
        <Grid
          item
          xs={12}
          lg={8}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Categories Card */}
          <div
            className="menu-card"
            style={{ padding: "20px", marginBottom: "20px" }}
          >
            <div className="menu-title">
              <span>{"Categories"}</span>
            </div>

            <WayfinderCard
              items={filteredCategories}
              userId={userId}
              buttonColor={buttonColor}
              placeholder="Enter new category name"
              inputValue={frontCategoryName}
              onInputChange={(e) => setFrontCategoryName(e.target.value)}
              onSubmit={async () => {
                if (frontCategoryName.trim()) {
                  await createCategory(frontCategoryName);
                  setFrontCategoryName("");
                }
              }}
              handleToggleVisibility={(id, isVisible) =>
                toggleCategoryVisibility(id, isVisible, true)
              }
              onDelete={(id) => deleteCategory(id, true)}
              type="category"
              submitButtonText="+"
              onViewSubCategories={handleViewSubCategories}
              currentLanguage={currentLanguage}
            />
          </div>

          {/* Subcategories Card */}
          {showSubCategoryView && (
            <Fade in={showSubCategoryView}>
              <Paper
                sx={{
                  borderRadius: 3,
                  border: "1px solid #ddd",
                  width: "100%",
                  mb: 4,
                  maxWidth: "800px",
                }}
              >
                <SubCategoryView
                  category={selectedCategoryForSubView}
                  onClose={handleCloseSubCategoryView}
                  userId={userId}
                  buttonColor={buttonColor}
                  existingSubCategories={frontendSubCategories}
                  allSubCategories={allSubCategories}
                  onCreateNew={handleCreateSubCategoryFromView}
                  onLinkExisting={handleLinkExistingSubCategory}
                  onUnlink={handleUnlinkSubCategory}
                  handleToggleVisibility={(id, isVisible) =>
                    toggleSubCategoryVisibility(id, isVisible)
                  }
                  onDelete={(subcategoryId, categoryId) =>
                    deleteSubCategory(subcategoryId, categoryId)
                  }
                  frontendCategories={frontendCategories}
                  selectedParentCategories={selectedParentCategories}
                  setSelectedParentCategories={setSelectedParentCategories}
                  toggleCategoryVisibility={toggleCategoryVisibility}
                  deleteCategory={deleteCategory}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  filteredSubCategories={filteredSubCategories}
                  currentLanguage={currentLanguage}
                />
              </Paper>
            </Fade>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// Wrap with layout
const CategoryManagerPageWithLayout = () => (
  <WayfinderLayout>
    <CategoryManagerPage />
  </WayfinderLayout>
);

export default CategoryManagerPageWithLayout;
